import { borderRadius, palette, spaces } from '@kimuno/theme';
import { Box, Button, FormLabel, SvgIcon, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import type React from 'react';
import { useState } from 'react';
import type { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import getMuiIcon from '../../utils/getMuiIcon';
import { useId } from '../../utils/useId';

type LabelledTextFieldProps = {
	label?: string;
	value?: string;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	placeholder?: string;
	dataTestId?: string;
	name?: string;
	isPasswordTextField?: boolean;
	registration?: UseFormRegisterReturn;
	error?: FieldError;
	multiline?: boolean;
	maxLength?: number;
	/** Shows `{length}/{maxLength}` below the input; needs a controlled `value`. */
	showCharacterCount?: boolean;
	minRows?: number;
	required?: boolean;
	'aria-describedby'?: string;
};

const LabelledTextField: React.FC<LabelledTextFieldProps> = (props) => {
	const { t } = useTranslation();
	const [showPassword, setShowPassword] = useState(false);
	const {
		label,
		value,
		onChange,
		placeholder,
		dataTestId,
		name,
		isPasswordTextField,
		registration,
		error,
		multiline,
		maxLength,
		showCharacterCount,
		minRows = 2,
		required,
		'aria-describedby': ariaDescribedBy,
	} = props;

	const { ref: registrationRef, ...registrationField } =
		registration ?? ({} as Partial<UseFormRegisterReturn>);
	const errorMessage = error?.message?.toString() ?? '';
	const fieldId = useId(name);
	const labelId = `${fieldId}-label`;
	const errorId = `${fieldId}-error`;
	const characterCountId = `${fieldId}-character-count`;
	const helperTextId = error ? errorId : undefined;
	const hasCharacterCount = showCharacterCount === true && maxLength !== undefined;
	const describedBy =
		[ariaDescribedBy, hasCharacterCount ? characterCountId : undefined, helperTextId]
			.filter(Boolean)
			.join(' ') || undefined;

	// The count has to sit between the input and the error, and the error is the
	// field's helper text — so both share it. The alert role moves onto the error
	// alone, or every keystroke would be announced with it.
	const helperText = hasCharacterCount ? (
		<>
			<Typography
				id={characterCountId}
				component="span"
				variant="caption"
				sx={{ display: 'block', color: palette.text.secondary }}
			>
				{(value ?? '').length}/{maxLength} {t('characters')}
			</Typography>
			{error && (
				<Box component="span" id={errorId} role="alert" sx={{ display: 'block' }}>
					{t(errorMessage)}
				</Box>
			)}
		</>
	) : (
		t(errorMessage)
	);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			{label && (
				<FormLabel id={labelId} htmlFor={fieldId} sx={{ color: palette.text.primary }}>
					{label}
				</FormLabel>
			)}
			<TextField
				id={fieldId}
				value={value}
				onChange={(event) => {
					registrationField.onChange?.(event);
					onChange?.(event as React.ChangeEvent<HTMLInputElement>);
				}}
				onBlur={registrationField.onBlur}
				placeholder={placeholder}
				type={isPasswordTextField && !showPassword ? 'password' : 'text'}
				name={name ?? registrationField.name}
				multiline={multiline}
				variant="outlined"
				inputRef={registrationRef}
				helperText={helperText}
				error={error ? true : false}
				minRows={multiline ? minRows : undefined}
				required={required}
				inputProps={{
					maxLength: maxLength,
					'aria-labelledby': label ? labelId : undefined,
					'aria-describedby': describedBy,
					'aria-invalid': error ? true : undefined,
					'aria-required': required ? true : undefined,
					style: multiline
						? {
								padding: 0,
							}
						: {
								paddingTop: spaces.xs,
								paddingBottom: spaces.xs,
								paddingLeft: spaces.sm,
								paddingRight: isPasswordTextField ? 0 : spaces.sm,
							},
					'data-testid': dataTestId,
				}}
				FormHelperTextProps={
					hasCharacterCount
						? undefined
						: {
								id: helperTextId,
								role: error ? 'alert' : undefined,
							}
				}
				sx={{
					'& .MuiOutlinedInput-root': {
						paddingRight: isPasswordTextField ? 0 : spaces.sm,
					},
				}}
				InputProps={{
					endAdornment: isPasswordTextField ? (
						<Button
							onClick={() => setShowPassword(!showPassword)}
							aria-label={showPassword ? t('Hide password') : t('Show password')}
							aria-pressed={showPassword}
							type="button"
						>
							{showPassword ? (
								<SvgIcon component={getMuiIcon('eyeClosed')} sx={{ padding: 0 }} aria-hidden="true" />
							) : (
								<SvgIcon component={getMuiIcon('eyeOpen')} sx={{ padding: 0 }} aria-hidden="true" />
							)}
						</Button>
					) : null,
					sx: {
						borderRadius: borderRadius.sm,
						paddingRight: isPasswordTextField ? spaces.sm : 0,
						padding: multiline ? `${spaces.xs} ${spaces.sm}` : undefined,
					},
				}}
			/>
		</Box>
	);
};

export default LabelledTextField;
